import { type ReactNode, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Card } from "~/features/configurator/Linear/components/Card";
import type { ContractDetailsResponse } from "~/features/contracts/models/models";
import type { AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import { Box, Button, Stack, styled, Typography } from "~/common/mui";
import { Link, LinkType } from "~/common/components/Link";
import { isPackageRestrictionEnabled } from "~/features/PackageRestriction/utils/isPackageRestrictionEnabled";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { ImageFormat } from "~/domains/product/types/ProductDataDTO";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import type { PopupData } from "~/features/contracts/components/AddToCartPopup/ContractVersionContents";
import {
  ContractLineItemStatus,
  CONTRACT_LINE_ITEM_STATUS_CODES,
} from "~/features/contracts/models/contractLineItemStatus";
import AddContractToCartDialog from "~/features/contracts/components/AddToCartPopup/AddToCartPopup";
import { ContractAddToCartPopupStates } from "~/features/contracts/components/AddToCartPopup/ContractAddToCartPopupStates";
import { Alert } from "~/common/mui/Alert/Alert";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { type AddToCartItem, addToCart } from "~/features/addToCart/redux/addToCart/productAddFlow";
import type { AppDispatch } from "~/app/store";
import { useDispatch } from "react-redux";

const SummaryRoot = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "repeat(4, 1fr)",
  rowGap: theme.spacing(3),
  [theme.breakpoints.up("desktop-s")]: {
    gridTemplateColumns: "repeat(4, 1fr)",
    gridTemplateRows: "100%",
    columnGap: theme.spacing(3),
  },
}));

const SubGrid = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: theme.spacing(2),
}));

const NetSumValue = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.up("desktop-m")]: {
    alignItems: "center",
    flexDirection: "row",
    gap: theme.spacing(0.5),
  },
}));

const TileCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

interface NewContractDetailsSummaryProps {
  contractDetails: ContractDetailsResponse;
  productAvailability?: AvailabilityInfo;
  className?: string;
}

const transformContractDetailsToATCItem = (
  contractDetails: ContractDetailsResponse,
  popupData: PopupData,
  productAvailability: AvailabilityInfo | undefined,
): AddToCartItem => {
  const { quantity, requestedDeliveryDate } = popupData;

  const packageCode = contractDetails.packCode;
  const packageSize = contractDetails.packSize;

  const packagingInfo =
    productAvailability?.packagingInformation &&
    productAvailability.packagingInformation.filter((item) => item.packCode === packageCode.toString())[0];

  const primaryWarehouseCode = packagingInfo && packagingInfo.primaryWarehouseCode;

  return {
    productCode: contractDetails.product.code,
    packageSize: packageSize,
    packageCode: packageCode.toString(),
    quantity: quantity,
    comment: "",
    selectedDeliveryDate: requestedDeliveryDate,
    selectedDeliveryDateIsManual: true,
    primaryWarehouseCode: primaryWarehouseCode ?? "",
    contractLine: contractDetails.lineNumber.toString() ?? "",
    contractNumber: contractDetails.schaefflerContractNumber,
    // Contracts are a fixed price. No price reference here
    priceReference: "",

    restrictedPackage: isPackageRestrictionEnabled(),
    originCountry: "", // TODO: Provide origin country for contracts
    contractOpenQuantity: contractDetails.openQuantity,
  };
};

// copied from contract actions, refactored to not use redux
//TODO: investigate how both functions can be brought back together
const contractItemAddToCart = (
  dispatch: AppDispatch,
  contract: ContractDetailsResponse,
  data: PopupData,
  availability?: AvailabilityInfo,
  onSuccess?: () => void,
  onError?: () => void,
) => {
  const contractItem = transformContractDetailsToATCItem(contract, data, availability);

  if (!contractItem) {
    return undefined;
  }

  dispatch(
    addToCart({
      items: [contractItem],
      page: "CONTRACT_DETAILS",
      source: "unknown",
      successUi: "modal",
      errorUi: "none",
    }),
  )
    .then(() => {
      onSuccess?.();
    })
    .catch((err) => {
      console.error(err);
      onError?.();
    });
};

const Tile = ({ title, children }: { title: string; children?: ReactNode }) => {
  return (
    <TileCard>
      <Stack gap={2}>
        <Typography variant="titleMedium" fontWeight="bold">
          {title}
        </Typography>
        <div>{children}</div>
      </Stack>
    </TileCard>
  );
};

const InfoEntry = ({ label, value }: { label: string; value: ReactNode }) => {
  return (
    <Stack gap={0.5}>
      <Typography variant="labelLarge" color="text.secondary">
        {label}
      </Typography>
      <div>{value}</div>
    </Stack>
  );
};

const NetSumTile = ({
  nextDisposition,
  quantity,
  netPrice,
  totalPrice,
  contractDetails,
  predecessor,
  productAvailability,
}: {
  nextDisposition: string;
  quantity: string;
  netPrice: ContractDetailsResponse["nettoPrice"];
  totalPrice: ContractDetailsResponse["totalPrice"];
  contractDetails: ContractDetailsResponse;
  predecessor?: { detailsUrl?: string; contractNumber?: string };
  productAvailability?: AvailabilityInfo;
}) => {
  const dispatch = useDispatch();
  const [popupContractStatus, setPopupContractStatus] = useState<ContractAddToCartPopupStates>(
    ContractAddToCartPopupStates.LOADED,
  );
  const { val: popup, setTrue: open, setFalse: close } = useBooleanState(false);
  const hasPredecessor = !!predecessor?.contractNumber;
  const { t } = useTranslation();

  const mappedStatus =
    CONTRACT_LINE_ITEM_STATUS_CODES[contractDetails.status as keyof typeof CONTRACT_LINE_ITEM_STATUS_CODES] ??
    undefined;
  const isActionable =
    mappedStatus !== ContractLineItemStatus.Cancelled && mappedStatus !== ContractLineItemStatus.Completed;

  return (
    <Stack gap={4}>
      <Stack gap={1} sx={{ borderBottom: 1, borderColor: "divider", pb: 4 }}>
        <div className="d-flex flex-justify-between">
          <div>{t("contractOverview.table.disposition")}</div>
          <div>{nextDisposition}</div>
        </div>
        <div className="d-flex flex-justify-between">
          <div>{t("contractDetails.nextDispositionQuantity")}</div>
          <div>{quantity}</div>
        </div>
        <div className="d-flex flex-justify-between">
          <div>{t("common.product.netPricePerPiece:::one")}</div>
          <div>{netPrice.formattedValue}</div>
        </div>
        <Stack direction="row" justifyContent="space-between" sx={{ typography: "bodyLarge", fontWeight: "bold" }}>
          <NetSumValue>
            <Typography variant="bodyLarge" fontWeight="bold">
              {t("wishlistPage.total")}
            </Typography>
            <Typography variant="labelLarge" color="text.secondary">
              {t("cartPage.taxes")}
            </Typography>
          </NetSumValue>
          <div>{totalPrice.formattedValue}</div>
        </Stack>
      </Stack>
      <Stack gap={2}>
        <Button
          onClick={open}
          startIcon={<ShoppingCartIcon />}
          disabled={hasPredecessor || !isActionable}
          clta-details-page-element-marker={"add-to-cart"}
          clta-product-name={contractDetails.product.designation}
          clta-product-id={contractDetails.product.sapMaterialNumber}
          clta-total-value={totalPrice.value.toString()}
        >
          {t("recentlyViewProducts.addProducts")}
        </Button>
        {hasPredecessor && (
          <Alert severity="info">
            <p>{t("contractDetails.basedOnPrevious")}</p>
            <Trans i18nKey="contractDetails.pleaseComplete" values={{ contractNumber: predecessor?.contractNumber }}>
              Please complete
              <Link className="f-bold" href={predecessor?.detailsUrl} variant={LinkType.NAVIGATION}>
                contract {predecessor?.contractNumber}
              </Link>
              first.
            </Trans>
          </Alert>
        )}
        {!isActionable && !hasPredecessor && <Alert severity="info">{t("contractDetails.atc.cancelled")}</Alert>}
      </Stack>

      {popup && (
        <AddContractToCartDialog
          contractDetails={contractDetails}
          contractStatus={popupContractStatus}
          product={contractDetails.product}
          onClose={() => {
            close();
            setPopupContractStatus(ContractAddToCartPopupStates.LOADED);
          }}
          onAdd={(e) => {
            setPopupContractStatus(ContractAddToCartPopupStates.LOADING);
            contractItemAddToCart(
              dispatch,
              contractDetails,
              e,
              productAvailability,
              () => {
                close();
                setPopupContractStatus(ContractAddToCartPopupStates.LOADED);
              },
              () => {
                setPopupContractStatus(ContractAddToCartPopupStates.SEND_FAILURE);
              },
            );
          }}
        />
      )}
    </Stack>
  );
};

const NewContractDetailsSummary = ({
  contractDetails,
  productAvailability,
  className,
}: NewContractDetailsSummaryProps) => {
  const { t } = useTranslation();
  const nextDisposition =
    contractDetails.nextDisposition && formatDate(contractDetails.nextDisposition, DateFormatType.DATE);
  const contractPeriod =
    contractDetails.contractValidFrom &&
    contractDetails.contractValidTo &&
    `${formatDate(contractDetails.contractValidFrom, DateFormatType.DATE)} - ${formatDate(
      contractDetails.contractValidTo,
      DateFormatType.DATE,
    )}`;
  const datePlaced = contractDetails.datePlaced && formatDate(contractDetails.datePlaced, DateFormatType.DATE);
  const { product, shipToAddress } = contractDetails;
  return (
    <SummaryRoot>
      <Tile title={t("newContract.contractTile")}>
        <Stack gap={2}>
          <InfoEntry label={t("contractOverview.table.contractNo")} value={contractDetails.customerContractNumber} />
          <InfoEntry
            label={t("contractDetailsPage.summary.schaContractNo")}
            value={contractDetails.schaefflerContractNumber}
          />
          <SubGrid>
            <InfoEntry label={t("contractOverview.table.period")} value={contractPeriod ?? "-"} />
            <InfoEntry label={t("orderDetailsPage.summary.date")} value={datePlaced ?? "-"} />
          </SubGrid>
          <InfoEntry
            label={t("contractOverview.table.netPrice")}
            value={<span className="f-bold">{contractDetails.totalPrice.formattedValue}</span>}
          />
          <SubGrid>
            <InfoEntry label={t("contractDetails.totals.totalQty")} value={contractDetails.quantityRequested} />
            <InfoEntry label={t("contractDetails.openQuantity")} value={contractDetails.openQuantity} />
          </SubGrid>
        </Stack>
      </Tile>
      <Tile title={t("feedback.option.invoiceProduct")}>
        <Stack gap={2}>
          <Stack gap={2} direction="row">
            <Link href={product.url}>
              <img width={64} src={product.images?.find((image) => image.format === ImageFormat.thumbnail)?.url} />
            </Link>

            <Box sx={{ alignItems: "start" }}>
              <Link variant={LinkType.NAVIGATION} href={product.url}>
                <span className="f-bold">{product.designation}</span>
              </Link>
            </Box>
          </Stack>
          <InfoEntry
            label={t("addToCart.contract.packaging")}
            value={t("addToCartPopup.packSize", { amount: contractDetails.packSize, code: contractDetails.packCode })}
          />
          <InfoEntry
            label={t("productComparison.download.schaefflerId")}
            value={contractDetails.product.sapMaterialNumber}
          />
          <InfoEntry
            label={t("orderListPage.queryType.customMaterialNumber")}
            value={contractDetails.customerMaterial}
          />
        </Stack>
      </Tile>
      <Tile title={t("newContract.shippingTile")}>
        <Stack gap={2}>
          <InfoEntry
            label={t("checkoutPage.headers.shippingAddress")}
            value={
              <Stack gap={0}>
                <span className="f-bold">{shipToAddress?.companyName}</span>
                <span>{`${shipToAddress?.lines?.map((line) => `${line} `)}`}</span>
                <span>{`${shipToAddress?.postalCode ?? ""} ${shipToAddress?.town ?? ""}`}</span>
                <span>{t(shipToAddress?.country?.name ?? "")}</span>
              </Stack>
            }
          />
          <InfoEntry label={t("cartPage.shipmentDetails.freightPayment")} value={t("contractDetailsPage.freight")} />
        </Stack>
      </Tile>
      <NetSumTile
        nextDisposition={nextDisposition ?? "-"}
        quantity={contractDetails.nextDispositionQuantity.toString()}
        netPrice={contractDetails.nettoPrice}
        totalPrice={contractDetails.totalPrice}
        contractDetails={contractDetails}
        productAvailability={productAvailability}
        predecessor={contractDetails.contractPredecessor}
      />
    </SummaryRoot>
  );
};
export default NewContractDetailsSummary;
